/**
 * Astro-form values, validation, and server formatters.
 *
 * Pure functions only (no React) so they're trivial to reason about and reuse.
 * Validation is driven by the server `meta` flags — place/time are required only
 * when the backend says so (parity with askk-android: place mandatory, time not).
 */
import { AstroFormMeta } from '../../api/types'

export type Gender = 'M' | 'F'

/**
 * Raw form state. DOB and time are kept as separate string parts (matching the
 * boxed inputs in the design) and only assembled into server formats on submit.
 */
export interface AstroFormValues {
  name: string
  gender: Gender
  dobDay: string
  dobMonth: string
  dobYear: string
  birthPlace: string | null
  birthHours: string
  birthMinutes: string
  birthIsAm: boolean
}

export interface AstroFormErrors {
  name?: string
  dob?: string
  birthPlace?: string
  birthTime?: string
}

export const INITIAL_VALUES: AstroFormValues = {
  name: '',
  gender: 'M',
  dobDay: '',
  dobMonth: '',
  dobYear: '',
  birthPlace: null,
  birthHours: '',
  birthMinutes: '',
  birthIsAm: true,
}

// Letters + spaces only — same rule as Android's nameRegex.
const NAME_REGEX = /^[a-zA-Z\s]+$/

/** A complete, real, past calendar date (rejects e.g. 31-02 via round-trip). */
export function isValidDob(day: string, month: string, year: string): boolean {
  if (year.length !== 4) return false
  const d = Number(day)
  const m = Number(month)
  const y = Number(year)
  if (!d || !m || !y) return false
  const date = new Date(y, m - 1, d)
  const realDate = date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d
  // Strictly in the past: reject today and any future date (compare to midnight today).
  const startOfToday = new Date()
  startOfToday.setHours(0, 0, 0, 0)
  return realDate && date.getTime() < startOfToday.getTime()
}

/** True once both hour and minute have been entered. */
export function hasBirthTime(v: AstroFormValues): boolean {
  return v.birthHours.length > 0 && v.birthMinutes.length > 0
}

/* ---- Per-field validators (used on blur and, composed, on submit) ---- */

export function validateName(v: AstroFormValues): string | undefined {
  const name = v.name.trim()
  if (!name) return 'Please enter your name'
  if (!NAME_REGEX.test(name)) return 'Name can only contain letters'
  return undefined
}

/**
 * On blur (requireComplete=false): flag only an out-of-range part or a fully
 * entered but invalid date — a half-typed date is NOT an error yet. On submit
 * (requireComplete=true): a missing/incomplete date is an error too. Never
 * mutates the user's input.
 */
export function validateDob(
  v: AstroFormValues,
  opts: { requireComplete?: boolean } = {}
): string | undefined {
  const { dobDay: d, dobMonth: m, dobYear: y } = v
  if (m && (Number(m) < 1 || Number(m) > 12)) return 'Enter a valid month (1–12)'
  if (d && (Number(d) < 1 || Number(d) > 31)) return 'Enter a valid day (1–31)'
  if (y.length === 4 && (Number(y) < 1900 || Number(y) > new Date().getFullYear())) {
    return 'Enter a valid year'
  }
  const complete = !!d && !!m && y.length === 4
  if (complete) {
    if (!isValidDob(d, m, y)) return 'Please enter a valid date of birth'
  } else if (opts.requireComplete) {
    return 'Please enter your date of birth'
  }
  return undefined
}

/**
 * Time is optional. Flags an out-of-range part or a half-filled time (one of
 * hour/minute without the other); requires a time only when meta says so.
 */
export function validateTime(v: AstroFormValues, meta: AstroFormMeta): string | undefined {
  const { birthHours: h, birthMinutes: mm } = v
  if (h && (Number(h) < 1 || Number(h) > 12)) return 'Enter a valid hour (1–12)'
  if (mm && (Number(mm) < 0 || Number(mm) > 59)) return 'Enter valid minutes (00–59)'
  if (!!h !== !!mm) return 'Enter both hour and minute'
  if (meta.isBirthTimeMandatory && !hasBirthTime(v)) return 'Please enter your time of birth'
  return undefined
}

/** Full validation for submit — every field, completeness required. */
export function validate(v: AstroFormValues, meta: AstroFormMeta): AstroFormErrors {
  return {
    name: validateName(v),
    dob: validateDob(v, { requireComplete: true }),
    birthPlace:
      meta.isBirthPlaceMandatory && !v.birthPlace ? 'Please select your place of birth' : undefined,
    birthTime: validateTime(v, meta),
  }
}

export function hasErrors(errors: AstroFormErrors): boolean {
  return Object.values(errors).some(Boolean)
}

/* ---- Server formatters ---- */

/** → "DD-MM-YYYY" (zero-padded). */
export function formatDob(day: string, month: string, year: string): string {
  return `${day.padStart(2, '0')}-${month.padStart(2, '0')}-${year}`
}

/** → "HH:MM AM/PM", or null when no time was entered. */
export function formatTime(v: AstroFormValues): string | null {
  if (!hasBirthTime(v)) return null
  return `${v.birthHours.padStart(2, '0')}:${v.birthMinutes.padStart(2, '0')} ${v.birthIsAm ? 'AM' : 'PM'}`
}
